/**
 * 预览区域组件
 * 显示当前配置的预览信息
 */
import React from 'react';
import { Card, Tag, Empty, Descriptions } from 'antd';
import { UserOutlined, TeamOutlined, BankOutlined, CodeOutlined } from '@ant-design/icons';
import { useUserAssignmentStore } from './useUserAssignmentStore';
import { ASSIGNMENT_TYPE_CONFIG, ASSIGNMENT_TYPES } from './constants';

const PreviewArea = () => {
  // 直接从store获取状态
  const selectedType = useUserAssignmentStore(state => state.selectedType)
  const selectedUsers = useUserAssignmentStore(state => state.selectedUsers)
  const selectedRoles = useUserAssignmentStore(state => state.selectedRoles)
  const selectedDepts = useUserAssignmentStore(state => state.selectedDepts)
  const expression = useUserAssignmentStore(state => state.expression)
  const config = ASSIGNMENT_TYPE_CONFIG[selectedType];
  
  if (!config) {
    return (
      <Card title="预览" style={{ height: '100%' }}>
        <Empty description="暂无配置信息" />
      </Card>
    );
  }

  const renderPreviewContent = () => {
    const items = [];
    
    // 基本信息
    items.push({
      key: 'type',
      label: '分配类型',
      children: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {config.icon}
          <span style={{ marginLeft: '8px' }}>{config.label}</span>
          <Tag 
            color={config.isDynamic ? 'orange' : 'blue'} 
            size="small" 
            style={{ marginLeft: '8px' }}
          >
            {config.isDynamic ? '动态' : '静态'}
          </Tag>
        </div>
      )
    });
    
    // 用户信息
    if (selectedType === ASSIGNMENT_TYPES.STATIC_USER && config.needsUserSelect && selectedUsers?.length > 0) {
      items.push({
        key: 'users',
        label: '选择用户',
        children: (
          <div>
            {selectedUsers.map(user => (
              <Tag key={user.value} icon={<UserOutlined />} className="mb-1">
                {user.Name}
              </Tag>
            ))}
            <div className="mt-1 text-xs text-gray-600">
              共 {selectedUsers.length} 个用户
            </div>
          </div>
        )
      });
    }
    
    // 角色信息
    if (selectedType === ASSIGNMENT_TYPES.STATIC_ROLE && config.needsRoleSelect && selectedRoles?.length > 0) {
      items.push({
        key: 'roles',
        label: '选择角色',
        children: (
          <div>
            {selectedRoles.map(role => (
              <Tag key={role.value} icon={<TeamOutlined />} color="blue" className="mb-1">
                {role.Name}
              </Tag>
            ))}
            <div className="mt-1 text-xs text-gray-600">
              共 {selectedRoles.length} 个角色
            </div>
          </div>
        )
      });
    }
    
    // 部门信息
    if (selectedType === ASSIGNMENT_TYPES.STATIC_DEPT && config.needsDeptSelect && selectedDepts?.length > 0) {
      items.push({
        key: 'depts',
        label: '选择部门',
        children: (
          <div>
            {selectedDepts.map(dept => (
              <Tag key={dept.value} icon={<BankOutlined />} color="green" className="mb-1">
                {dept.Name}
              </Tag>
            ))}
            <div className="mt-1 text-xs text-gray-600">
              共 {selectedDepts.length} 个部门
            </div>
          </div>
        )
      });
    }
    
    // 动态部门+角色
    if (selectedType === ASSIGNMENT_TYPES.DYNAMIC_DEPT_ROLE) {
      if (selectedDepts?.length > 0) {
        items.push({
          key: 'dynamic_depts',
          label: '动态部门',
          children: (
            <div>
              {selectedDepts.map(dept => (
                <Tag key={dept.Id || dept.value} icon={<BankOutlined />} color="green" className="mb-1">
                  {dept.Name || dept.label}
                </Tag>
              ))}
              <div className="mt-1 text-xs text-gray-600">
                共 {selectedDepts.length} 个部门
              </div>
            </div>
          )
        });
      }
      
      if (selectedRoles?.length > 0) {
        items.push({
          key: 'dynamic_roles',
          label: '动态角色',
          children: (
            <div>
              {selectedRoles.map(role => (
                <Tag key={role.Id || role.value} icon={<TeamOutlined />} color="blue" className="mb-1">
                  {role.Name || role.label}
                </Tag>
              ))}
              <div className="mt-1 text-xs text-gray-600">
                共 {selectedRoles.length} 个角色
              </div>
            </div>
          )
        });
      }
    }
    
    // 表达式
    if (selectedType === ASSIGNMENT_TYPES.EXPRESSION && expression) {
      items.push({
        key: 'expression',
        label: '表达式',
        children: (
          <div>
            <Tag icon={<CodeOutlined />} color="purple">
              自定义表达式
            </Tag>
            <div className="mt-2 p-2 bg-gray-100 rounded font-mono text-xs break-all">
              {expression}
            </div>
          </div>
        )
      });
    }
    
    // 描述信息
    items.push({
      key: 'description',
      label: '说明',
      children: (
        <div className="text-xs text-gray-600 leading-relaxed">
          {config.description}
        </div>
      )
    });
    
    return (
      <Descriptions 
        column={1} 
        size="small"
        items={items}
        labelStyle={{ fontWeight: 'bold', width: '80px' }}
        contentStyle={{ paddingLeft: '16px' }}
      />
    );
  };

  return (
    <Card 
      title="预览" 
      className="h-full overflow-hidden"
      styles={{
        body: {
          maxHeight: 'calc(50vh - 40px)', 
          overflowY: 'auto',
          padding: '16px'
        }
      }}
    >
      {renderPreviewContent()}
    </Card>
  );
};

export default PreviewArea;